"use client";
import { useCallback, useMemo } from "react";

type UseInfiniteScrollProps = {
  arrayLength: number;
  loading?: boolean;
  fetchMore: (params: { variables: { offset: number } }) => Promise<any>;
} & (
  | {
      fixedLimit: number;
      maxLength?: never;
    }
  | {
      fixedLimit?: never;
      maxLength: number;
    }
);

interface UseInfiniteScrollReturn {
  hasMore: boolean;
  onLoadMore: () => Promise<void>;
}

export const useInfiniteScroll = ({
  arrayLength,
  loading = false,
  fetchMore,
  fixedLimit,
  maxLength,
}: UseInfiniteScrollProps): UseInfiniteScrollReturn => {
  const hasMore = useMemo(() => {
    if (loading) return false;

    if (maxLength !== undefined) {
      return maxLength > arrayLength;
    }

    if (fixedLimit !== undefined) {
      return arrayLength % fixedLimit === 0 && arrayLength > 0;
    }

    return false;
  }, [arrayLength, loading, maxLength, fixedLimit]);

  const onLoadMore = useCallback(async () => {
    if (!hasMore || loading) return;

    try {
      await fetchMore({ variables: { offset: arrayLength } });
    } catch (error) {
      console.error("Error loading more items:", error);
    }
  }, [hasMore, loading, fetchMore, arrayLength]);

  return {
    hasMore,
    onLoadMore,
  };
};
